import React from 'react';
import PlayingCard from 'components/PlayingCard';

export interface AdminCardView {
  code: string;
  rank?: number;
  suit?: string;
}

function normalizeCards(raw: unknown): AdminCardView[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') return { code: item };
        if (item && typeof item === 'object' && 'code' in item) {
          return item as AdminCardView;
        }
        return null;
      })
      .filter(Boolean) as AdminCardView[];
  }
  if (typeof raw === 'string') {
    try {
      return normalizeCards(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  return [];
}

function cardKey(list: AdminCardView[]): string {
  return list
    .map((c) => c.code)
    .sort()
    .join(',');
}

function CardRow({ list, compact }: { list: AdminCardView[]; compact?: boolean }) {
  return (
    <div className="tp-cards-flat-row flex items-end">
      {list.map((c, i) => (
        <PlayingCard
          key={`${c.code}-${i}`}
          code={c.code}
          small
          compact={compact}
          flatLayout
          fanIndex={i}
        />
      ))}
    </div>
  );
}

export function AdminHandCards({
  cards,
  resolvedCards,
  handCategory,
  folded,
  compact,
}: {
  cards?: unknown;
  resolvedCards?: unknown;
  handCategory?: string | null;
  folded?: boolean;
  compact?: boolean;
}) {
  const dealt = normalizeCards(cards);
  const ranked = normalizeCards(resolvedCards);
  if (!dealt.length) {
    return <span className="text-xs text-muted">—</span>;
  }

  const showRanked = ranked.length > 0 && cardKey(ranked) !== cardKey(dealt);

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      {showRanked ? (
        <>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-muted mb-0.5">Dealt</p>
            <CardRow list={dealt} compact={compact} />
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-amber-800 mb-0.5">
              Ranked as
            </p>
            <CardRow list={ranked} compact={compact} />
          </div>
        </>
      ) : (
        <CardRow list={dealt} compact={compact} />
      )}
      {folded && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-subtle text-muted border border-line inline-block">
          Packed
        </span>
      )}
      {handCategory && (
        <p className={`text-[10px] font-semibold text-indigo-700 ${compact ? '' : 'mt-0.5'}`}>
          {handCategory}
        </p>
      )}
    </div>
  );
}

export default AdminHandCards;
